/**
 * PDT Status Component
 * Displays Pattern Day Trading compliance status
 */

import { usePDTStatus } from '../api/client';

export function PDTStatus() {
  const { data, isLoading, error } = usePDTStatus();

  if (isLoading) {
    return (
      <div className="card">
        <div className="loading">Loading PDT status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">Error loading PDT status: {error.message}</div>
      </div>
    );
  }

  const pdt = data?.pdtStatus;
  if (!pdt) return null;

  const statusBadge =
    pdt.tradingMode === 'EMERGENCY'
      ? 'badge-danger'
      : pdt.tradingMode === 'CONSERVATIVE'
      ? 'badge-warning'
      : 'badge-success';

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">PDT Status</h2>
        <span className={`card-badge ${statusBadge}`}>{pdt.tradingMode}</span>
      </div>

      {pdt.warningMessage && (
        <div
          className="error"
          style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            fontSize: '0.875rem',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid var(--accent-yellow)',
            color: 'var(--accent-yellow)',
          }}
        >
          <strong>Warning:</strong> {pdt.warningMessage}
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        {/* Account Balance */}
        <div className="metric">
          <div className="metric-label">Account Balance</div>
          <div className="metric-value">
            ${pdt.accountBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Above Threshold */}
        <div className="metric">
          <div className="metric-label">PDT Status</div>
          <div
            className="metric-value"
            style={{
              color: pdt.isAboveThreshold ? 'var(--accent-green)' : 'var(--accent-yellow)',
            }}
          >
            {pdt.isAboveThreshold ? 'Above $25k' : 'Below $25k'}
          </div>
        </div>

        {/* Day Trades Used */}
        <div className="metric">
          <div className="metric-label">Day Trades Used</div>
          <div
            className="metric-value"
            style={{
              color: pdt.dayTradesUsed >= 3 ? 'var(--accent-red)' : 'var(--text-primary)',
            }}
          >
            {pdt.dayTradesUsed} / 3
          </div>
        </div>

        {/* Day Trades Remaining */}
        <div className="metric">
          <div className="metric-label">Day Trades Remaining</div>
          <div
            className="metric-value"
            style={{
              color:
                pdt.dayTradesRemaining === 0
                  ? 'var(--accent-red)'
                  : pdt.dayTradesRemaining === 1
                  ? 'var(--accent-yellow)'
                  : 'var(--accent-green)',
            }}
          >
            {pdt.isAboveThreshold ? '∞' : pdt.dayTradesRemaining}
          </div>
        </div>

        {/* Can Day Trade */}
        <div className="metric">
          <div className="metric-label">Can Day Trade</div>
          <div
            className="metric-value"
            style={{
              color: pdt.canDayTrade ? 'var(--accent-green)' : 'var(--accent-red)',
            }}
          >
            {pdt.canDayTrade ? 'YES' : 'NO'}
          </div>
        </div>

        {/* Trading Mode */}
        <div className="metric">
          <div className="metric-label">Trading Mode</div>
          <div className="metric-value" style={{ fontSize: '1rem' }}>
            {pdt.tradingMode}
          </div>
        </div>
      </div>

      {/* Rolling Window */}
      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
          5-Business-Day Rolling Window
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          {new Date(pdt.windowStart).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })}{' '}
          —{' '}
          {new Date(pdt.windowEnd).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })}
        </div>
      </div>
    </div>
  );
}
